import axios from 'axios';

// 1. Point to your Backend URL (Ensure this matches your server port)
const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // IMPORTANT: To send/receive cookies if needed
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('my_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const PUBLIC_PATHS = new Set([
  '/',
  '/login',
  '/about-us',
  '/terms',
  '/faq',
  '/privacy',
  '/refund',
  '/delete',
  '/price',
]);

const isPublicPath = (pathname) => {
  if (PUBLIC_PATHS.has(pathname)) {
    return true;
  }

  return pathname.startsWith('/template-details/');
};

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 2. Fix Status Check: Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('my_refresh_token');

      if (!refreshToken) {
        logoutUser();
        return Promise.reject(error);
      }

      try {
        // 3. Match your Backend Controller logic
        // Your controller expects { refresh_token } in body or cookie
        const response = await axios.post(`${BASE_URL}/auth/refresh-token`, {
          refresh_token: refreshToken
        });

        // 4. Fix Data Path: Your controller returns { status: 'success', data: { access_token } }
        if (response.data.status === 'success') {
          const newAccessToken = response.data.data.access_token;
          localStorage.setItem('my_access_token', newAccessToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          return api(originalRequest);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        logoutUser();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Helper to clear storage and redirect
const logoutUser = () => {
  localStorage.removeItem('my_access_token');
  localStorage.removeItem('my_refresh_token');
  localStorage.removeItem('user_data');
  if (!isPublicPath(window.location.pathname)) {
    window.location.href = '/login';
  }
};



export const authAPI = {
  // Matches: router.post('/send-otp', sendOTP)
  sendOTP: (data) => api.post('/auth/send-otp', data),

  // Matches: router.post('/resend-otp', resendOTP)
  resendOTP: (data) => api.post('/auth/resend-otp', data),

  // Matches: router.post('/verify-otp', verifyOTP)
  verifyOTP: (data) => api.post('/auth/verify-otp', data),

  // Matches: router.post('/refresh-token', refreshToken)
  refreshToken: (data) => api.post('/auth/refresh-token', data),

  // Matches: router.post('/logout', logout)
  logout: () => api.post('/auth/logout'),

  // Profile endpoints (Add these to your backend later)
  getProfile: () => api.get('/profile'),
  updateProfile: (data) => api.put('/profile/update', data),
};

export const templateAPI = {

  getTemplatesDisplay: (params) => {

    const { groupId, subGroupId = 'all' } = params;
    return api.get(`/v1/templates-display?groupId=${groupId}&subGroupId=${subGroupId}`);
  },
  getDesignGroups: () => api.get('/v1/design-groups'),

  getTemplateDetails: (templateId) => api.get(`/v1/template/${templateId}`),

  createDesignEntry: (templateId) => api.post(`/v1/template/${templateId}/create-entry`),
  updateDesignEntry: (entryId, data) => api.put(`/v1/design-entries/${entryId}`, data),
  getDownloads: (params) => api.get('/v1/downloads', { params })
};

export const walletAPI = {
  buyCredit: () => api.get('/wallet/buy-credits'),

  showTransactions: () => api.get('/wallet/transactions'),
};

export const apiUtils = {
  // Handle API errors
  handleError: (error) => {
    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data?.message || 'An error occurred',
        status: error.response.status,
        data: error.response.data,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error. Please check your connection.',
        status: 0,
        data: null,
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
        data: null,
      };
    }
  },

  // Format API response
  formatResponse: (response) => {
    return {
      success: true,
      data: response.data,
      message: response.data?.message || 'Success',
      status: response.status,
    };
  },

  // Check if response is successful
  isSuccess: (response) => {
    return response.status >= 200 && response.status < 300;
  },
};

export default api;